import React, { useRef, useState, useEffect } from "react";
import { Button } from "primereact/button";
import "../../../directors.scss";
import {
  ApiResponse,
  DirectorInformation,
  DirectorInformationRequestBody,
  MappedAppointments,
} from "~/Pages/Directors/types/director";
import AddDirectorAppointmentModal from "~/Pages/Directors/modals/AddDirectorAppointmentModal";
import DuplicateInformationCard from "./DuplicateInformationCard";
import API_ROUTES_V2 from "~/api-routes-v2";
import apiService from "~/services/service";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "primereact/skeleton";

interface DirectorMatchingResultProps {
  data: Array<DirectorInformation>;
  loading?: boolean;
  setIsSubmittedSuccessfully?: (value: boolean) => void;
}
const DirectorMatchingResult = ({
  data,
  loading,
  setIsSubmittedSuccessfully,
}: DirectorMatchingResultProps) => {
  const cardContainerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false);
  const [appointments, setAppointments] = useState<MappedAppointments[] | []>(
    [],
  );
  const [isAppointmentLoading, setIsAppointmentLoading] = useState(false);
  const [selectedDirector, setSelectedDirector] =
    useState<DirectorInformation | null>(null);
  const [editAppointmentEntry, setEditAppointmentEntry] = useState(null);
  const [isEditAppointment, setIsEditAppointment] = useState(false);
  const { entityid } = useParams();
  useEffect(() => {
    const el = cardContainerRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, [data, loading]);

  const handleCarouselDirection = (direction: "left" | "right") => {
    if (direction === "left") {
      cardContainerRef.current?.scrollBy({ left: -350, behavior: "smooth" });
    }
    if (direction === "right") {
      cardContainerRef.current?.scrollBy({ left: 350, behavior: "smooth" });
    }
  };
  const updateDirectorInformation = async () => {
    const requestBody = {};

    if (selectedDirector?.entities?.length) {
      requestBody.entities = [...selectedDirector.entities, entityid];
    }
    try {
      await apiService(API_ROUTES_V2.UPDATE_DIRECTOR, "PUT", {
        replace: { id: selectedDirector?.id },
        body: JSON.stringify(requestBody),
      });
    } catch (error) {
      console.error("Error saving Director Information: ", error);
    }
  };
  const handleAddAsDirector = (director: DirectorInformation) => {
    setSelectedDirector(director);
    setShowAddAppointmentModal(true);
    setIsSubmittedSuccessfully?.(true);
  };
  const addDirectorAppointmentHistory = async (
    directorId: string,
    requestBody: Partial<DirectorInformationRequestBody>,
  ) => {
    const body = {
      ...requestBody,
      entity_id: entityid,
    };

    try {
      if (!directorId) return;
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.DIRECTOR_APPOINTMENT_HISTORY,
        "POST",
        {
          replace: { directorId, entityId: entityid || "" },
          body: JSON.stringify(body || {}),
        },
      );
      await updateDirectorInformation();
      navigate(`/entity/${entityid}/directors/${directorId}/info`, {
        state: { refetchUsers: true },
      });
      return response;
    } catch (error) {
      throw new Error("Failed to add director appointment history");
    }
  };
  const getTitle = () => {
    const isAllLinkedToEntity = data?.every(
      (director) => director.is_linked_to_entity,
    );
    if (isAllLinkedToEntity) {
      return {
        title: "Oops! This Director is already linked to this entity.",
        description:
          "We discovered similar profiles that match the entered data to avoid duplicates.",
      };
    }
    return {
      title: "Oops! This Director might already be registered.",
      description:
        "Look for duplicates. We discovered similar profiles that match the entered data to avoid duplicates.",
    };
  };
  return (
    <div className="director__matching-container">
      {data?.length > 0 && (
        <div className="heading-container">
          <h4 className="heading">{getTitle().title}</h4>
          <p className="desc">{getTitle().description}</p>
        </div>
      )}
      <div>
        {loading ? (
          <div className="cards-container" ref={cardContainerRef}>
            {[...Array(3)].map(() => {
              return (
                <div className="director__information__card">
                  <div className="flex align-items-center mb-3">
                    {/* Avatar */}
                    <Skeleton
                      shape="circle"
                      width="9rem"
                      height="6.5rem"
                      className="mr-2"
                    />
                    <div className="card__detail w-full">
                      <Skeleton width="60%" height="1rem" className="mb-2" />

                      <Skeleton width="40%" height="1rem" className="mb-2" />

                      <Skeleton width="50%" height="1rem" className="mb-2" />

                      <Skeleton width="70%" height="1rem" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Skeleton width="100%" height="2.5rem" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="cards-container" ref={cardContainerRef}>
            {data.map((director) => {
              return (
                <DuplicateInformationCard
                  duplicate={director}
                  onAddAsDuplicate={handleAddAsDirector}
                  type="director"
                />
              );
            })}
          </div>
        )}

        {isOverflowing && (
          <div className="carousel-button-container">
            <Button
              type="button"
              outlined
              icon="pi pi-angle-left"
              onClick={() => handleCarouselDirection("left")}
            />
            <Button
              type="button"
              outlined
              icon="pi pi-angle-right"
              onClick={() => handleCarouselDirection("right")}
            />
          </div>
        )}
      </div>
      <AddDirectorAppointmentModal
        visible={showAddAppointmentModal}
        onHideModal={() => {
          setIsEditAppointment(false);
          setEditAppointmentEntry(null);
          setShowAddAppointmentModal(false);
          setIsSubmittedSuccessfully?.(false);
        }}
        setAppointments={setAppointments}
        editAppointmentEntry={editAppointmentEntry}
        isEditAppointment={isEditAppointment}
        appointments={appointments}
        addAsDirectorAndShareholder="director"
        directorId={selectedDirector?.id}
        isEditMode={true}
        addDirectorAppointmentHistory={addDirectorAppointmentHistory}
        isLoading={isAppointmentLoading}
        setIsLoading={setIsAppointmentLoading}
      />
    </div>
  );
};
export default DirectorMatchingResult;
